import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { Input } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { Button, Files } from '~/ui/components';
import RadioSelector from '~/ui/components/RadioSelector/RadioSelector';
import { filteredStatus } from '~/common/tickets';
import { TicketManagementPriorities } from '~/typings/enums/Ticket/TicketPriorities';
import { createSelectValues } from '~/common/selectValues';
import { StyledSelect } from '../../../components/Actions/Actions.styles';
import { isFlow2Status } from '../whatsappTemplates';
import { StyledForm, Wrapper } from '../ResolveTicket.styles';

type StatusOption = { id: number; name: string; enabled: boolean };
type ResolvingArea = { id: number; name: string };
type ResolutionReason = { id: number; justification: string; journeys: number[]; status: number[] };

export type FormValues = {
  resolvingAreaId: any;
  priorityTicket: string;
  ticketStatusId: number;
  justificationResolutionId?: any;
  files?: any[];
  comment?: string;
};

export type FormStepProps = {
  id: string;
  status: StatusOption[];
  resolvingAreas: ResolvingArea[];
  ticketDetail: any;
  isLoadingResolvingAreas: boolean;
  isLoadingStatus: boolean;
  resolutionReasons: ResolutionReason[];
  isLoadingResolutionReasons: boolean;
  isResolving?: boolean;
  onReview: (data: FormValues, step: 'compose' | 'resolve') => void;
};

function FormStep({
  status,
  resolvingAreas,
  ticketDetail,
  isLoadingResolvingAreas,
  isLoadingStatus,
  resolutionReasons,
  isLoadingResolutionReasons,
  isResolving,
  onReview,
}: FormStepProps) {
  const form = useRef<WrappedFormUtils>();

  const updatedStatus = useMemo(() => {
    const filteredOptions = filteredStatus(status, 'CREATE_TICKET');
    const pendingClientStatus = (status || [])?.find((option) => option?.id === 276 && option?.enabled);
    const availableOptions = pendingClientStatus ? [...filteredOptions, pendingClientStatus] : filteredOptions;
    return availableOptions
      .filter((option, index, array) => array.findIndex((item) => item?.id === option?.id) === index)
      .map((option) => ({ label: option.name, value: option.id }));
  }, [status]);

  // sem pré-seleção: o status da resolução exige escolha ATIVA do agente
  // (pré-marcar o status atual induzia a "resolver" sem mudar nada)
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);

  const selectedStatusName = useMemo(
    () => status?.find((item) => item?.id === selectedStatus)?.name || '',
    [status, selectedStatus],
  );

  const isFlow2 = useMemo(
    () => !ticketDetail?.companyId && isFlow2Status(selectedStatusName),
    [selectedStatusName, ticketDetail?.companyId],
  );

  const filteredResolutionReasons = useMemo(() => {
    if (!resolutionReasons) return [];
    const preSelectedId = ticketDetail?.justificationResolutionId;

    if (!selectedStatus) {
      if (!preSelectedId) return [];
      const preSelected = resolutionReasons.find((r) => r.id === preSelectedId);
      return preSelected ? [preSelected] : [];
    }

    // ticket sem journey não pode zerar as opções (QA TC28: justificativas
    // vazias no resolver via flutuante) — sem journeyId, filtra só por status
    // A justificativa pré-existente do ticket NÃO é reinjetada quando
    // incompatível com o status escolhido (achado do ai-review na #1953):
    // manter a antiga na lista permitia enviar resolução inconsistente.
    return resolutionReasons.filter((reason) => {
      const matchesJourney = ticketDetail?.journeyId == null || reason.journeys?.includes(ticketDetail.journeyId);
      return matchesJourney && reason.status?.includes(selectedStatus);
    });
  }, [resolutionReasons, selectedStatus, ticketDetail]);

  const priorityOptions = useMemo(
    () =>
      Object.keys(TicketManagementPriorities).map((key) => ({
        value: key,
        label: TicketManagementPriorities[key],
      })),
    [],
  );

  const handleChangeStatus = useCallback(
    (value) => {
      setSelectedStatus(value);
      // justificativa selecionada que não vale para o novo status é limpa —
      // senão o form envia um par status/justificativa inconsistente
      const current = form.current?.getFieldValue('justificationResolutionId');
      const currentId = Array.isArray(current) ? current[current.length - 1] : current;
      if (currentId != null) {
        const stillValid = (resolutionReasons || []).some(
          (reason) =>
            reason.id === currentId &&
            reason.status?.includes(value) &&
            (ticketDetail?.journeyId == null || reason.journeys?.includes(ticketDetail.journeyId)),
        );
        if (!stillValid) form.current?.setFieldsValue({ justificationResolutionId: null });
      }
    },
    [resolutionReasons, ticketDetail],
  );

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const step = isFlow2 ? 'compose' : 'resolve';
      onReview(values, step);
    },
    [onReview, isFlow2],
  );

  useEffect(() => {
    if (!resolutionReasons?.length || !form.current) return;

    if (ticketDetail?.justificationResolutionId) {
      form.current.setFieldsValue({ justificationResolutionId: [ticketDetail.justificationResolutionId] });
    } else if (ticketDetail?.justification) {
      const matched = resolutionReasons.find((r) => r.justification === ticketDetail.justification);
      if (matched) {
        form.current.setFieldsValue({ justificationResolutionId: [matched.id] });
      }
    }
  }, [resolutionReasons]);

  const inputs = useMemo(
    () => [
      {
        id: 'resolvingAreaId',
        label: 'Área resolvedora',
        initialValue: createSelectValues(resolvingAreas, ticketDetail?.resolvingAreaId),
        input: (
          <StyledSelect
            popupPlacement="left"
            options={resolvingAreas}
            loading={isLoadingResolvingAreas}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        ),
        options: { rules: [{ required: true, message: 'Este campo é obrigatório' }] },
      },
      {
        id: 'priorityTicket',
        label: 'Prioridade',
        initialValue: ticketDetail?.priorityTicket,
        input: <RadioSelector options={priorityOptions} />,
        options: { rules: [{ required: true, message: 'Este campo é obrigatório' }] },
      },
      {
        id: 'ticketStatusId',
        label: 'Status da resolução',
        input: <RadioSelector onChange={handleChangeStatus} options={updatedStatus} loading={isLoadingStatus} />,
        options: { rules: [{ required: true, message: 'Este campo é obrigatório' }] },
      },
      {
        id: 'justificationResolutionId',
        label: 'Justificativa da resolução',
        initialValue: ticketDetail?.justificationResolutionId ? [ticketDetail.justificationResolutionId] : null,
        input: (
          <StyledSelect
            popupPlacement="left"
            options={filteredResolutionReasons}
            loading={isLoadingResolutionReasons}
            fieldNames={{ label: 'justification', value: 'id' }}
          />
        ),
        hidden: !selectedStatus,
      },
      {
        id: 'comment',
        label: 'Comentário',
        hidden: isFlow2,
        input: (
          <Input.TextArea
            placeholder="Escreva um comentário"
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={700}
            data-testid="form-comment-input"
          />
        ),
        options: {
          rules: [
            {
              validator: (_rule, value, callback) => {
                const text = value || '';
                if (text.length >= 700) {
                  callback(`Limite de 700 caracteres atingido (${text.length}/700). Reduza o texto para continuar.`);
                } else {
                  callback();
                }
              },
            },
          ],
        },
      },
      {
        id: 'files',
        skipArrayFormat: true,
        input: <Files />,
      },
    ],
    [
      isLoadingResolvingAreas,
      resolvingAreas,
      isLoadingStatus,
      isLoadingResolutionReasons,
      filteredResolutionReasons,
      ticketDetail,
      updatedStatus,
      priorityOptions,
      handleChangeStatus,
      selectedStatus,
    ],
  );

  return (
    <StyledForm ref={form} name="resolve-form" onSubmit={handleSubmit} inputs={inputs}>
      <Wrapper>
        <Button
          rounded
          fullWidth
          type="submit"
          disabled={!selectedStatus || (!isFlow2 && !!isResolving)}
          loading={!isFlow2 && !!isResolving}
          data-testid="review-button"
        >
          {isFlow2 ? 'Continuar' : 'Resolver'}
        </Button>
      </Wrapper>
    </StyledForm>
  );
}

export default FormStep;
